/* eslint-disable react/display-name */
import React, { useState, useEffect, useRef } from 'react';
import { css, StyleSheet } from 'aphrodite/no-important';
import { useHistory, useParams, userParams } from 'react-router-dom';
import { DataGrid, Drawer, TextInputWithLabel, Button, PopupMenu } from 'quantux-ui';
import AddUserDrawer from '../users-profiles/add-user-drawer';
import noPinIcon from '../../imgs/no-pin.svg';
import leftPinIcon from '../../imgs/left-pin.svg';
import rightPinIcon from '../../imgs/right-pin.svg';
import { BASE_URL, titleCase, USA_STATES } from '../../utils';
import { blue, gray, salmon } from '../../theme';

const defaultCellStyle = {
  font: '12px Lato',
  padding: '8px',
  display: 'flex',
  alignItems: 'center',
  // borderBottom: '0.5px solid lime',
  // borderRight: '0.5px solid lime',
  lineHeight: '14px'
};

const icons = {
  filter: { isSvg: false, path: '', class: 'fa fa-filter' },
  stringSortAscending: { isSvg: false, class: 'fa fa-alpha-asc' },
  stringSortDescending: { isSvg: false, class: 'fa fa-alpha-desc' },
  numberSortAscending: { isSvg: false, class: 'fa fa-numeric-asc' },
  numberSortDescending: { isSvg: false, class: 'fa fa-numeric-desc' },
  dateSortAscending: { isSvg: false, class: 'fa fa-alpha-asc' },
  dateSortDescending: { isSvg: false, class: 'fa fa-alpha-desc' },
  leftPin: { isSvg: true, path: leftPinIcon },
  rightPin: { isSvg: true, path: rightPinIcon },
  noPin: { isSvg: true, path: noPinIcon },
  menuIcon: { isSvg: false, class: 'fa fa-ellipsis-v' }
};

const DOCS_PER_PAGE = 10;
let controller = new AbortController();

const headerDefs = [
  // Provider
  {
    visible: true,
    dataType: 'string',
    pin: 'left',
    sortable: false,
    sortOrder: 0,
    enableCellClick: true,
    text: 'Provider',
    field: 'provider',
    key: 'provider',
    isCustomFilter: false,
    showFilterHint: false,
    filter: true,
    filterType: 'exactMatch',
    filterMenuType: 'basic', // possible  options => basic, advanced, none
    basicFilterType: 'exactMatch', // possible values are => startsWith, contains, exactMatch, endsWith
    advancedFilterType: 'containsAny', // possible values are => select, containsAll, conainsAny
    disabledFilterTypes: ['startsWith', 'contains', 'endsWith', 'advanced'],
    colStyles: { width: 281, minWidth: 281, maxWidth: 281, textAlign: 'left' },
    headerCellStyle: {},
    colCellsStyles: { textAlign: 'left' },
    footerCellStyle: {},
    cellRenderer: (text) => {
      const data = text.split('/');
      if (data.length > 2) {
        return (
          <React.Fragment>
            <div style={{ color: '#0080FF', cursor: 'pointer', fontSize: '12px' }}>
              {data[0]}
            </div>
            <div style={{ color: '#0080FF', cursor: 'pointer', fontSize: '12px' }}>
              {`NPI: ${data[1]}`}
            </div>
          </React.Fragment>
        );
      } else {
        return (
          <div style={{ color: '#0080FF', cursor: 'pointer', fontSize: '12px' }}>
            {data[0]}
          </div>
        );
      }
    }
  },
  // Speciality
  // Mailing Address
  {
    visible: true,
    dataType: 'string',
    pin: 'none',
    sortable: false,
    sortOrder: 0,
    enableCellClick: false,
    text: 'Primary Practice Address',
    field: 'mailingAddress',
    key: 'mailingAddress',
    showFilterHint: true,
    isCustomFilter: true,
    filterHint: 'Enter city, state code, or ZIP',
    filter: true,
    filterType: 'exactMatch',
    filterMenuType: 'basic', // possible  options => basic, advanced, none
    basicFilterType: 'exactMatch', // possible values are => startsWith, contains, exactMatch, endsWith
    advancedFilterType: 'containsAny', // possible values are => select, containsAll, conainsAny
    disabledFilterTypes: ['startsWith', 'exactMatch', 'contains', 'endsWith', 'advanced'],
    colStyles: { width: 240, minWidth: 240, maxWidth: 240, textAlign: 'left' },
    headerCellStyle: {},
    colCellsStyles: { textAlign: 'left' },
    footerCellStyle: {},
    cellRenderer: text => {
      // Format address and zip
      const addTmp = text.split(' ');
      let zip = addTmp.pop();
      zip = zip.length <= 5 ? zip : zip.substr(0, 5) + '-' + zip.substr(5, 9);
      // Generate Google map url
      // const addressUrl = "https://www.google.com/maps/search/?api=1&query=" + encodeURI(text);
      const addressUrl = 'https://www.google.com/maps/search/?api=1&query=' + encodeURI(`${addTmp.join(' ')} ${zip.substr(0, 5)}`);
      return (
        <div className="fx fx__v-center">
          <a
            target="_blank"
            href={addressUrl}
            rel="noreferrer"
            className="mr-2"
            style={{ fontSize: '16px' }}
          >
            <i className="fa fa-map-marker" />
          </a>
          <a
            target="_blank"
            href={addressUrl}
            rel="noreferrer"
            className="mr-2"
          >
            <div>
              {addTmp.join(' ') + ' ' + zip}
            </div>
          </a>
        </div>
      );
    }
  },
  // Phone
  {
    visible: true,
    dataType: 'string',
    pin: 'none',
    sortable: false,
    sortOrder: 0,
    enableCellClick: false,
    text: 'Phone',
    field: 'phone',
    key: 'phone',
    isCustomFilter: false,
    showFilterHint: false,
    filter: false,
    filterType: 'none',
    disabledFilterTypes: ['startsWith', 'exactMatch', 'contains', 'endsWith', 'advanced'],
    filterMenuType: 'advanced',
    basicFilterType: 'contains',
    advancedFilterType: 'containsAny',
    colStyles: { width: 123, minWidth: 123, maxWidth: 123, textAlign: 'left' },
    headerCellStyle: {},
    colCellsStyles: { textAlign: 'left' },
    footerCellStyle: {},
    cellRenderer: text => {
      if (text && text.length > 0) {
        return (
          <div>
            <a
              href={`tel:+1${text}`}
            >
              {`${text.substr(0, 3)}-${text.substr(3, 3)}-${text.substr(6, 4)}`}
            </a>
          </div>
        );
      } else {
        return <div>N/A</div>;
      }
    }
  },
  // Access Type
  {
    visible: true,
    dataType: 'string',
    pin: 'none',
    sortable: false,
    sortOrder: 0,
    enableCellClick: false,
    text: 'Access Type',
    field: 'accessType',
    key: 'accessType',
    isCustomFilter: false,
    showFilterHint: false,
    filter: false,
    filterType: 'none',
    disabledFilterTypes: ['startsWith', 'exactMatch', 'contains', 'endsWith', 'advanced'],
    filterMenuType: 'advanced',
    basicFilterType: 'contains',
    advancedFilterType: 'containsAny',
    colStyles: { width: 160, minWidth: 160, maxWidth: 160, textAlign: 'left' },
    headerCellStyle: {},
    colCellsStyles: { textAlign: 'left' },
    footerCellStyle: {}
  }
];

const FacilityProvidersGrid = (props) => {
  const [showLoader, setShowLoader] = useState(false);
  const [facilityMembers, setFacilityMembers] = useState([]);
  const [gridData, setGridData] = useState([]);
  const [filters, setFilters] = useState({});
  const [docsCount, setDocsCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  // -- Providers drawer
  const [showAddProviderDrawer, setShowAddProviderDrawer] = useState(false);
  const [practiceProvider, setPracticeProvider] = useState('');
  const [showSearchMenu, setShowSearchMenu] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [providerSearchResults, setProviderSearchResults] = useState([]);
  const [canAddPracticeProvider, setCanAddPracticeProvider] = useState(false);
  const [searchingProviders, setSearchingProviders] = useState(false);
  // --
  const [practiceProviders, setPracticeProviders] = useState([]);
  const [practiceProviderNpi, setPracticeProviderNpi] = useState('');
  const [practiceProviderId, setPracticeProviderId] = useState('');
  const [practiceProviderAvatarImageUrl, setPracticeProviderAvatarImageUrl] = useState('');
  // -- Provider search
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [npi, setNpi] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [showStateMenu, setShowStateMenu] = useState(false);
  const [showAddNewProviderDrawer, setShowAddNewProviderDrawer] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState({});
  const [canSearchForProviders, setCanSearchForProviders] = useState(false);
  // -- Refs
  const practiceProviderRef = useRef();
  const stateRef = useRef();

  // #region -- Network Ops ---------------------------------------------
  function searchPracticeOwners() {
    const url = new URL(`${BASE_URL}/users/searchMedPros`);
    const params = {
      searchTerm: practiceProvider,
      hasNpi: true
    };
    url.search = new URLSearchParams(params).toString();
    fetch(url)
      .then(res => res.text())
      .then((result) => {
        const data = JSON.parse(result);
        console.log(data);
        setSearchResults(data.users ? data.users : []);
      })
      .catch(error => console.log('error', error));
  };
  // #endregion

  const history = useHistory();
  // const { facilityNpi = 0 } = useParams();
  // -- Effects ---------------------------------------------------------
  // useEffect(() => {
  //   const ddF = setTimeout(() => {
  //     if (practiceProvider.length > 0) {
  //       searchPracticeOwners();
  //     }
  //   }, 300);
  //   return () => clearTimeout(ddF);
  // }, [practiceProvider]);

  useEffect(() => {
    getData();
  }, []);

  // -- Styles ----------------------------------------------------------
  const styles = StyleSheet.create({
    gridToolbar: {
      display: 'flex',
      alignItems: 'center',
      // justifyContent: 'space-between',
      justifyContent: 'flex-end',
      height: '40px',
      border: '1px solid black',
      borderBottom: 'none',
      paddingLeft: '4px',
      background: gray[500],
      width: '100%'
    },
    gridToolbarIcon: {
      height: '50px',
      width: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      ':hover': {
        color: blue[500]
      }
    },
    drawerContainer: {
      // background: gray[500],
      background: gray[800],
      color: gray[300],
      height: '100%',
      position: 'relative',
      display: 'flex',
      flexFlow: 'column'
    },
    drawerHeader: {
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 8px',
      fontWeight: 'bold',
      borderBottom: '1px solid black',
      flexShrink: '0'
    },
    drawerHeaderCloseIcon: {
      color: gray[300],
      ':hover': {
        color: salmon[500]
      }
    },
    drawerBanner: {
      position: 'relative',
      padding: '8px 0 0 2px',
      flexShrink: '0'
    },
    drawerBannerImg: {
      width: '100%',
      height: 'auto'
    },
    drawerBannerCameraIcon: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: gray[500],
      color: 'white',
      position: 'absolute',
      right: '0',
      bottom: '8px',
      fontSize: '11px',
      width: '24px',
      height: '24px',
      cursor: 'pointer',
      borderRadius: '50%'
    },
    drawerContentArea: {
      // height: 'calc(100vh - 186px)',
      overflowY: 'auto',
      color: gray[300],
      flexGrow: '1'
    },
    drawerUserCardAvatar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '80px',
      height: '80px',
      color: 'white',
      borderRadius: '4px',
      position: 'relative'
    },
    drawerUserCardAvatarImg: {
      width: 'auto',
      height: '80px',
      maxHeight: '80px'
    },
    drawerAvatarCamera: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: gray[500],
      color: 'white',
      position: 'absolute',
      left: '-3px',
      top: '58px',
      fontSize: '11px',
      width: '24px',
      height: '24px',
      cursor: 'pointer',
      borderRadius: '50%'
    },
    drawerUserCardText: {
      fontSize: '12px'
    },
    drawerFooter: {
      alignItems: 'center',
      justifyContent: 'flex-end',
      background: gray[700],
      height: '56px',
      width: '100%',
      borderTop: `1px solid ${gray[500]}`,
      flexShrink: '0'
    }
  });

  /**
   * Get grid data (List of all practice members inc providers & others)
   */
  const getData = async () => {
    setShowLoader(true);
    const formData = new FormData();
    formData.append('facilityNpi', props.facilityNpi);
    const reqHeaders = new Headers();
    const token = localStorage.getItem('token');
    reqHeaders.append('Authorization', `Bearer ${token}`);
    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow',
      headers: reqHeaders
    };
    const response = await fetch(`${BASE_URL}/facility/getFacilityProviders`, requestOptions);
    const result = await response.json();
    console.log(result);
    if (result.success) {
      const data = [];
      console.log(result.users);
      result.users.forEach(user => {
        const add1 = user.mailingAddress.streetAddress1;
        const suite = user.mailingAddress.suite;
        const city = user.mailingAddress.city;
        const state = user.mailingAddress.state;
        const zip = user.mailingAddress.zip;
        const accessType = user.accessType;
        console.log(`accessType = ${accessType}`);
        if (accessType !== 'Provide') {
          data.push({
            id: user._id || user.id,
            provider: !user.npi
              ? `${user.lastName}, ${user.firstName}/${user._id}`
              : `${user.lastName}, ${user.firstName}/${user.npi}/${user._id}`,
            mailingAddress: `${add1} ${suite} ${city}, ${state} ${zip}`,
            phone: user.phone ? user.phone.replace(/-/g, '') : '',
            accessType: accessType
          });
        }
      });
      setShowLoader(false);
      setFacilityMembers(result.users);
      setGridData(data);
    }
    setShowLoader(false);
  };

  /**
   * Grid cell onClick event handler
   */
  const handleOnClickCell = (col, value) => {
    console.log(col);
    console.log(value);
    localStorage.setItem('provider', value);
    console.log(gridData);
    const idx = gridData.findIndex(el => el.provider === value);
    const providerId = gridData[idx].id;
    history.push(`/provider/${providerId}/profile`);
  };

  /**
   * Grid toolbar + button onClick event handler (Edit providers list)
   */
  const handleOnClickAddProvider = () => {
    // const existingPracticeProviders = practiceMembers.filter(el => el.accessType === 'Provider');
    // console.log(existingPracticeProviders);
    // setPracticeProvider('');
    // setShowSearchMenu(false);
    // setSearchResults([]);
    // setPracticeProviders(existingPracticeProviders);
    // setCanAddPracticeProvider(false)

    setFirstName('');
    setLastName('');
    setCity('');
    setState('');
    setZip('');
    setProviderSearchResults([]);
    setShowAddProviderDrawer(true);
    setCanSearchForProviders(false);
  };

  /**
   * Search for providers (Both - Medpros and NPPES)
   */
  const handleOnClickAddPracticeProvider = () => {
    if (searchingProviders) {
      controller.abort();
      setSearchingProviders(false);
      setFirstName('');
      setLastName('');
      setNpi('');
      setCity('');
      setState('');
      setZip('');
      setCanSearchForProviders(false);
      return;
    }

    setProviderSearchResults([]);
    controller = new AbortController();
    const url = new URL(`${BASE_URL}/users/searchNppes`);
    const params = {
      firstName,
      lastName,
      npi,
      state: state === 'ALL' ? '' : state,
      city,
      zip
    };
    url.search = new URLSearchParams(params).toString();

    setSearchingProviders(true);
    fetch(url, { signal: controller.signal })
      .then(response => response.text())
      .then(result => {
        const data = JSON.parse(result);
        console.log(data.users);
        setSearchingProviders(false);
        data.users.sort((a, b) => b.hasMedprosProfile - a.hasMedprosProfile);
        setProviderSearchResults(data.users);
      })
      .catch(error => console.log('error', error));
  };

  /**
   * Update practice -- for providers list
   */
  const handleOnClickUpdateFacility = (provider) => {
    const myHeaders = new Headers();
    const token = localStorage.getItem('token');
    myHeaders.append('Authorization', `Bearer ${token}`);
    // -- Form Data
    const formData = new FormData();
    formData.append('facilityNpi', props.facilityNpi);
    formData.append('providerId', provider.id);

    const requestOptions = {
      method: 'POST',
      headers: myHeaders,
      body: formData,
      redirect: 'follow'
    };
    fetch(`${BASE_URL}/facility/updateFacilityProviders`, requestOptions)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        if (res.success) {
          setShowAddProviderDrawer(false);
          getData();
        }
      })
      .catch(error => console.log('error', error));
  };

  // #region -- Provider search event handlers
  const handleOnEditFirstName = (e) => {
    const { value } = e.target;
    setFirstName(value);
  };

  const handleOnEditLastName = (e) => {
    const { value } = e.target;
    setCanSearchForProviders(value.length > 0);
    setLastName(value);
  };

  const handleOnEditNpi = (e) => {
    const { value } = e.target;
    setNpi(value);
  };

  const handleOnEditCity = (e) => {
    const { value } = e.target;
    setCity(value);
  };

  const handleOnEditZip = (e) => {
    const { value } = e.target;
    setZip(value);
  };

  const handleOnClickAddProviderToFacility = (data) => {
    handleOnClickUpdateFacility({
      fullName: `${firstName} ${lastName}`,
      npi: +data.number,
      id: data.medprosId,
      accessType: 'Provider',
      avatarImageUrl: data.medprosAvatar
    });
  };

  const handleOnClickCreateProfileAndAddProvider = (data) => {
    setSelectedProvider(data);
    setShowAddNewProviderDrawer(true);
  };
  // #endregion

  return (
    <div style={{ width: '804px' }}>
      {/* Show add new provider drawer */}
      {showAddNewProviderDrawer &&
        <AddUserDrawer
          isVisible={showAddNewProviderDrawer}
          onClose={() => setShowAddNewProviderDrawer(false)}
          zIndex='2500'
          onAddProvider={handleOnClickUpdateFacility}
          data={selectedProvider}
        />
      }

      {/* Add provider to practice drawer */}
      {showAddProviderDrawer &&
        <Drawer
          placement="right"
          visible={showAddProviderDrawer}
          toggleDrawer={() => {
            setShowAddProviderDrawer(false);
          }}
          zIndex="2000"
          width={window.innerWidth <= 700 ? window.innerWidth : 358}
        >
          <div className={css(styles.drawerContainer)}>
            {/* Header */}
            <div className={css(styles.drawerHeader)}>
              <div className='fx fx__hv-center'>
                <i className='mr-2 fa fa-lg fa-user' />
                <div className='noselect'>Add Provider</div>
              </div>
              <i
                className={`fa fa-lg fa-times-circle ${css(styles.drawerHeaderCloseIcon)}`}
                onClick={() => {
                  setShowAddProviderDrawer(false);
                }}
              />
            </div>
            {/* Middle Area */}
            <div className={css(styles.drawerContentArea)}>
              <div className='p-1'>
                <div className='pl-2 pr-2'>
                  <div
                    className='mt-3'
                    ref={practiceProviderRef}
                  >
                    {/* First Name, Last Name and NPI */}
                    <div className='fx'>
                      {/* First Name */}
                      <TextInputWithLabel
                        value={firstName}
                        titleText="First Name"
                        titleFontSize={12}
                        isCompactLayout={true}
                        titleColor={gray[200]}
                        textColor="white"
                        selectionColor="white"
                        selectionBackground="#0080FF"
                        spellcheck={false}
                        width={108}
                        onChange={handleOnEditFirstName}
                      />
                      {/* Last Name */}
                      <TextInputWithLabel
                        value={lastName}
                        className='ml-2'
                        titleText="Last Name"
                        titleFontSize={12}
                        isCompactLayout={true}
                        titleColor={gray[200]}
                        textColor="white"
                        selectionColor="white"
                        selectionBackground="#0080FF"
                        spellcheck={false}
                        width={108}
                        placeholder='Required'
                        onChange={handleOnEditLastName}
                      />
                      {/* NPI */}
                      <TextInputWithLabel
                        value={npi}
                        className='ml-2'
                        titleText="NPI"
                        titleFontSize={12}
                        isCompactLayout={true}
                        titleColor={gray[200]}
                        textColor="white"
                        selectionColor="white"
                        selectionBackground="#0080FF"
                        spellcheck={false}
                        width={100}
                        onChange={handleOnEditNpi}
                      />
                    </div>
                    {/* City, State & Zip */}
                    <div className='fx' style={{ alignItems: 'flex-end' }}>
                      <TextInputWithLabel
                        value={city}
                        titleText="City"
                        titleFontSize={12}
                        isCompactLayout={true}
                        titleColor={gray[200]}
                        textColor="white"
                        selectionColor="white"
                        selectionBackground="#0080FF"
                        spellcheck={false}
                        width={192}
                        onChange={handleOnEditCity}
                      />
                      <div
                        ref={stateRef}
                        style={{ position: 'relative' }}
                      >
                        <div
                          className='ml-2'
                          onClick={() => setShowStateMenu(true)}
                          style={{
                            height: '24px',
                            width: '43px',
                            // background: blue[900],
                            background: '#103A60',
                            color: blue[200],
                            fontSize: '12px',
                            padding: '2px',
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative',
                            bottom: '-2px'
                          }}
                        >
                          {`${state.length === 0 ? 'ALL' : state} `}
                          <i style={{ fontSize: '16px' }} className={`ml-1 fa ${showStateMenu ? 'fa-caret-up' : 'fa-caret-down'}`} />
                        </div>
                        <PopupMenu
                          top={28}
                          left={11}
                          zIndex='1550'
                          position="absolute"
                          visible={showStateMenu}
                          parent={stateRef}
                          onOutsideClick={() => setShowStateMenu(false)}
                        >
                          <div
                            style={{
                              background: '#0a3250',
                              width: '128px',
                              height: '200px',
                              overflowY: 'auto'
                            }}
                          >
                            {USA_STATES.map(el => (
                              <div
                                key={el.code}
                                className='m-item'
                                onClick={() => {
                                  setState(el.code);
                                  setShowStateMenu(false);
                                }}
                                style={{
                                  height: '32px',
                                  borderBottom: '1px solid #001824',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  background: gray[700]
                                }}
                              >
                                {el.name}
                              </div>
                            ))}
                          </div>
                        </PopupMenu>
                      </div>
                      <TextInputWithLabel
                        value={zip}
                        className='ml-2'
                        titleText="Zip"
                        titleFontSize={12}
                        isCompactLayout={true}
                        titleColor={gray[200]}
                        textColor="white"
                        selectionColor="white"
                        selectionBackground="#0080FF"
                        spellcheck={false}
                        width={80}
                        onChange={handleOnEditZip}
                      />
                    </div>
                    {/* Search Button */}
                    <Button
                      className='mt-4'
                      btnType='primary'
                      isDisabled={lastName.length === 0}
                      showLoader={searchingProviders}
                      onClick={handleOnClickAddPracticeProvider}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        height: '32px'
                      }}
                    >
                      {searchingProviders ? 'Searching... Click to Cancel' : 'Search'}
                    </Button>
                  </div>
                  {/* Facility Providers Cards */}
                  <div
                    className='mt-2 pl-2 pr-2'
                    style={{
                      height: 'calc(100vh - 270px)',
                      overflowY: 'auto'
                    }}
                  >
                    {providerSearchResults.map(el => {
                      const {
                        first_name: firstName,
                        last_name: lastName
                      } = el.basic;

                      const taxIdx = el.taxonomies.findIndex(el => el.primary);
                      const addIdx = el.addresses.findIndex(el => el.address_purpose === 'MAILING');
                      const duplicateIndex = facilityMembers.findIndex(provider => {
                        return provider.accessType === 'Provider' && provider.npi === +el.number;
                      });
                      return (
                        <div
                          key={el.number}
                          className='fx mt-1 p-1'
                          style={{
                            background: blue[900],
                            borderRadius: '4px',
                            fontSize: '14px',
                            fontWeight: '700'
                          }}
                        >
                          {/* Data */}
                          <div style={{ flexGrow: '1' }}>
                            <div style={{ color: blue[400] }}>
                              <div>
                                {`${titleCase(lastName)}, ${titleCase(firstName)}`}
                              </div>
                              <div>
                                {el.number}
                              </div>
                            </div>
                            <div className='mt-1'>
                              <div>{el.taxonomies[taxIdx].desc}</div>
                              <div>
                                {`${titleCase(el.addresses[addIdx].city)}, ${el.addresses[addIdx].state.toUpperCase()}`}
                              </div>
                            </div>
                          </div>
                          {/* Icons/buttons */}
                          <div className='pr-1'>
                            <Button
                              isDisabled={duplicateIndex > -1}
                              style={{
                                maxWidth: '130px',
                                minWidth: '130px',
                                maxHeight: '32px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginTop: '4px',
                                fontSize: '14px'
                              }}
                              onClick={() => {
                                if (el.hasMedprosProfile) {
                                  handleOnClickAddProviderToFacility(el);
                                } else {
                                  handleOnClickCreateProfileAndAddProvider(el);
                                }
                              }}
                            >
                              {el.hasMedprosProfile ? 'Add Provider' : 'Create Profile'}
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Drawer>
      }

      {/* Grid Toolbar */}
      <div className={css(styles.gridToolbar)}>
        {/* Add provider button */}
        <div
          className={css(styles.gridToolbarIcon)}
          onClick={handleOnClickAddProvider}
        >
          <i className="fa fa-lg fa-plus-circle" />
        </div>
      </div>
      <DataGrid
        loader={showLoader}
        icons={icons}
        headerDefs={headerDefs}
        rowData={gridData}
        filters={filters}
        gridWidth={804}
        gridHeight={300}
        gridHeaderHeight={48}
        gridFooterHeight={0}
        gridContainerHeight="804px"
        rowHeight={40}
        rowsPerPage={DOCS_PER_PAGE}
        defaultCellStyle={defaultCellStyle}
        showFooter={false}
        showEllipsisCol
        enableHover
        totalPages={Math.ceil(docsCount / DOCS_PER_PAGE)}
        currentPage={currentPage}
        onApplyFilter={() => console.log('not implemented yet!')}
        onResetCol={() => console.log('not implemented yet!')}
        onLoadPage={() => console.log('not implemented yet!')}
        onClickCell={handleOnClickCell}
        onColFilterToggle={() => console.log('not implemented yet!')}
        onFilterTypeUpdate={() => console.log('not implemented yet!')}
        onColPinChange={() => console.log('not implemented yet!')}
        onResize={() => console.log('on resize')}
        onPopupMenu={() => console.log('On popup menu')}
        onChangeTextAlignment={() => console.log('Text alignment changed!')}
        onFilterChange={() => console.log('on filter changed')}
        onColVisibilityChange={() => console.log('on col visibility changed')}
        onResetGrid={() => console.log('on reset grid')}
        onSort={() => { console.log('Column sort order changed!'); }}
        onColConfig={() => console.log('on col config changed')}
      />
    </div>
  );
};

export default FacilityProvidersGrid;
